/*
 * The in-app user manual, as data.
 *
 * Content lives here rather than in JSX so it stays readable, diffable and searchable;
 * components/manual/ManualBlocks.js is the only place that knows how a block renders.
 * Written for this (develop) version of the tool. There is no printable PDF on this line
 * yet; the HL line's docs/user-manual.pdf describes that version, not this one.
 *
 * ─── Section shape ──────────────────────────────────────────────────────────────────
 *   {
 *     id:    'upload',                      // URL fragment; must be unique
 *     group: 'Bring calls in',              // heading it sits under in the contents rail
 *     title: 'Upload recordings',
 *     lede:  'One line on what the page is for.',
 *     roles: ['sAdm', 'adm', 'qa'],         // who can actually use it; see ROLE_LABELS
 *     blocks: [ ... ]                       // rendered in order
 *   }
 *
 * ─── Block shapes ───────────────────────────────────────────────────────────────────
 *   { type: 'p',     text }
 *   { type: 'h3',    text }
 *   { type: 'ul',    items: [ text | { text, items: [text] } ] }
 *   { type: 'steps', items: [ { title, text, items?: [text] } ] }
 *   { type: 'note',  variant: 'info'|'tip'|'caution'|'danger', tag, text }
 *   { type: 'table', caption?, head: [text], rows: [[text]], footnote? }
 *   { type: 'defs',  items: [ { term, text } ] }
 *
 * ─── Inline markup, usable in any text field ────────────────────────────────────────
 *   **bold**            emphasis
 *   `Save`              a control the reader will look for on screen
 *   {{Disputes|#id}}    a link to another section
 */

import gettingStarted from './sections/gettingStarted';
import setup from './sections/setup';
import ingestion from './sections/ingestion';
import reviewing from './sections/reviewing';
import afterReview from './sections/afterReview';
import administration from './sections/administration';
import reference from './sections/reference';

export const ROLE_LABELS = Object.freeze({
  sAdm: 'Super Admin',
  adm: 'Admin',
  qa: 'QA Agent',
  callAgent: 'Call Agent',
});

export const ROLE_SHORT = Object.freeze({
  sAdm: 'SA',
  adm: 'A',
  qa: 'QA',
  callAgent: 'CA',
});

export const MANUAL_SECTIONS = [
  ...gettingStarted,
  ...setup,
  ...ingestion,
  ...reviewing,
  ...afterReview,
  ...administration,
  ...reference,
];

// The contents rail groups sections by the `group` on each one, in the order they
// first appear — so the rail order is the reading order, with no second list to
// keep in step.
export function manualGroups(sections = MANUAL_SECTIONS) {
  const groups = [];
  sections.forEach((section) => {
    const existing = groups.find((g) => g.name === section.group);
    if (existing) {
      existing.sections.push(section);
    } else {
      groups.push({ name: section.group, sections: [section] });
    }
  });
  return groups;
}

export function sectionsForRoles(roles) {
  if (!roles || roles.length === 0) return MANUAL_SECTIONS;
  return MANUAL_SECTIONS.filter((section) => section.roles.some((r) => roles.includes(r)));
}

// Flattens a section's text so search can match anything the reader can see, not just
// the heading. Inline markers are stripped so searching "Save New Version" matches
// text written as `Save New Version`.
function sectionText(section) {
  const parts = [section.title, section.lede];

  const pushBlock = (block) => {
    if (block.text) parts.push(block.text);
    if (block.caption) parts.push(block.caption);
    if (block.footnote) parts.push(block.footnote);
    if (block.head) parts.push(block.head.join(' '));
    if (block.rows) block.rows.forEach((row) => parts.push(row.join(' ')));
    if (block.items) {
      block.items.forEach((item) => {
        if (typeof item === 'string') {
          parts.push(item);
        } else {
          parts.push(item.term, item.title, item.text);
          if (item.items) parts.push(item.items.join(' '));
        }
      });
    }
  };

  section.blocks.forEach(pushBlock);
  return parts.filter(Boolean).join(' ').replace(/[*`{}]/g, '').toLowerCase();
}

const SEARCH_INDEX = new Map(MANUAL_SECTIONS.map((s) => [s.id, sectionText(s)]));

export function searchSections(query, sections = MANUAL_SECTIONS) {
  const needle = query.trim().toLowerCase();
  if (!needle) return sections;
  return sections.filter((section) => (SEARCH_INDEX.get(section.id) || '').includes(needle));
}
